import React, { useState, useEffect } from 'react'
import { useI18n } from '../../i18n'
import { API_ENDPOINTS } from '../../config/api'
import { X } from 'lucide-react'
import { componentScope } from '../../lib/cssScope'
import { Button } from '../ui/Button'
import { Select } from '../ui/Select'
import { TextField } from '../ui/TextField'
import modalStyles from './SupplierFormModal.module.css'

const CATEGORY_ACCESS_OPTIONS = [
  { value: 'No Access', label: 'No Access' },
  { value: 'With Access', label: 'With Access' },
] as const

const SUPPLIER_TYPE_OPTIONS = [
  { value: 'Services Supplier', label: 'Services Supplier' },
  { value: 'Software Supplier', label: 'Software Supplier' },
  { value: 'Hardware Supplier', label: 'Hardware Supplier' },
  { value: 'Services Supplier (Including Cloud)', label: 'Services Supplier (Including Cloud)' },
  { value: 'Administrative Economic Materials and Services', label: 'Administrative Economic Materials and Services' },
] as const

interface AddSupplierModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
  token: string
}

const AddSupplierModal: React.FC<AddSupplierModalProps> = ({ isOpen, onClose, onSuccess, token }) => {
  const { t } = useI18n()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [availableStandards, setAvailableStandards] = useState<string[]>([])
  const [formData, setFormData] = useState({
    name: '',
    contactName: '',
    email: '',
    website: '',
    categoryAccess: 'No Access',
    supplierType: 'Services Supplier',
    subGradation: '',
    standards: [] as string[],
    compliance: 0
  })

  // Load all available standards from dashboard
  useEffect(() => {
    const loadStandards = async () => {
      try {
        // Load compliance standards
        const complianceRes = await fetch(API_ENDPOINTS.COMPLIANCE_STANDARDS, {
          headers: { Authorization: `Bearer ${token}` }
        })
        const complianceData = await complianceRes.json()
        
        // Load supplier-only standards
        const supplierRes = await fetch(API_ENDPOINTS.TPSM_STANDARDS, {
          headers: { Authorization: `Bearer ${token}` }
        })
        const supplierData = await supplierRes.json()
        
        // Map standard codes to normalized database codes
        const standardCodeMap: Record<string, string> = {
          'GDPR': 'GDPR',
          'ISO27001': 'ISO_27001',
          'ISO_27001': 'ISO_27001',
          'ISO42001': 'ISO_42001',
          'ISO_42001': 'ISO_42001',
          'SOC2': 'SOC2',
          'SOC 2': 'SOC2',
          'DORA': 'DORA',
          'NIS2': 'NIS2',
          'PCI_DSS': 'PCI_DSS_2025',
          'PCI_DSS_2025': 'PCI_DSS_2025',
          'SWIFT_CSCF_2025': 'SWIFT_CSCF_2025',
          'SDLC_SECURITY': 'SDLC_SECURITY',
          'TECH_CONTROLS': 'TECH_CONTROLS',
          'TECHNICAL_CONTROLS': 'TECH_CONTROLS',
          'ENS_ES': 'ENS_ES',
          'BSI_DE': 'BSI_DE',
          'ANSSI_FR': 'ENS_ANSSI',
          'ENS_ANSSI': 'ENS_ANSSI',
          'KSBP_LT': 'KSBP_LT',
          'NCA_EE': 'NCA_EE',
          'CIS': 'CIS',
        }
        
        const standardsSet = new Set<string>()
        
        // Add compliance standards (normalize codes)
        if (Array.isArray(complianceData)) {
          complianceData.forEach((std: any) => {
            if (std.code) {
              const normalized = standardCodeMap[std.code] || std.code
              standardsSet.add(normalized)
            }
          })
        } else if (complianceData.data && Array.isArray(complianceData.data)) {
          complianceData.data.forEach((std: any) => {
            if (std.code) {
              const normalized = standardCodeMap[std.code] || std.code
              standardsSet.add(normalized)
            }
          })
        }
        
        // Add supplier-only standards (normalize codes)
        if (Array.isArray(supplierData)) {
          supplierData.forEach((std: any) => {
            if (std.code) {
              const normalized = standardCodeMap[std.code] || std.code
              standardsSet.add(normalized)
            }
          })
        }
        
        // Add fallback standards if API didn't return any (only valid ones)
        if (standardsSet.size === 0) {
          Object.values(standardCodeMap).forEach(std => standardsSet.add(std))
        }
        
        // Remove duplicates and sort - use normalized database codes only
        const uniqueStandards = Array.from(standardsSet)
          .filter(std => Object.values(standardCodeMap).includes(std))
          .filter((std, index, arr) => arr.indexOf(std) === index) // Remove duplicates
          .sort()
        
        setAvailableStandards(uniqueStandards.length > 0 ? uniqueStandards : ['GDPR', 'ISO_27001', 'ISO_42001', 'SOC2', 'DORA', 'NIS2', 'PCI_DSS_2025', 'SWIFT_CSCF_2025', 'SDLC_SECURITY', 'TECH_CONTROLS', 'ENS_ES', 'BSI_DE', 'ENS_ANSSI', 'KSBP_LT', 'NCA_EE'])
      } catch (e) {
        console.warn('Failed to load standards, using defaults:', e)
        // Fallback to default standards
        setAvailableStandards(['GDPR', 'ISO_27001', 'ISO_42001', 'SOC2', 'DORA', 'NIS2', 'PCI_DSS_2025', 'SWIFT_CSCF_2025', 'SDLC_SECURITY', 'TECH_CONTROLS', 'ENS_ES', 'BSI_DE', 'ENS_ANSSI', 'KSBP_LT', 'NCA_EE'])
      }
    }
    
    if (isOpen && token) {
      loadStandards()
    }
  }, [isOpen, token])

  if (!isOpen) return null

  // Normalize standard codes to match database codes
  const normalizeStandards = (standards: string[]): string[] => {
    const standardCodeMap: Record<string, string> = {
      'ISO27001': 'ISO_27001',
      'ISO_27001': 'ISO_27001',
      'ISO42001': 'ISO_42001',
      'ISO_42001': 'ISO_42001',
      'SOC 2': 'SOC2',
      'SOC2': 'SOC2',
      'GDPR': 'GDPR',
      'DORA': 'DORA',
      'NIS2': 'NIS2',
      'PCI_DSS': 'PCI_DSS_2025',
      'PCI_DSS_2025': 'PCI_DSS_2025',
      'SWIFT_CSCF_2025': 'SWIFT_CSCF_2025',
      'SDLC_SECURITY': 'SDLC_SECURITY',
      'TECH_CONTROLS': 'TECH_CONTROLS',
      'TECHNICAL_CONTROLS': 'TECH_CONTROLS',
      'ENS_ES': 'ENS_ES',
      'BSI_DE': 'BSI_DE',
      'ANSSI_FR': 'ENS_ANSSI',
      'ENS_ANSSI': 'ENS_ANSSI',
      'KSBP_LT': 'KSBP_LT',
      'NCA_EE': 'NCA_EE',
      'CIS': 'CIS',
    }
    
    return standards
      .map(std => standardCodeMap[std] || std)
      .filter((std, index, arr) => arr.indexOf(std) === index) // Remove duplicates
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    try {
      const res = await fetch(API_ENDPOINTS.SUPPLIERS, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          ...formData,
          standards: normalizeStandards(formData.standards),
          lastAssessment: new Date().toISOString()
        })
      })

      const data = await res.json()
      
      if (res.ok) {
        onSuccess()
        onClose()
        setFormData({
          name: '',
          contactName: '',
          email: '',
          website: '',
          categoryAccess: 'No Access',
          supplierType: 'Services Supplier',
          subGradation: '',
          standards: [],
          compliance: 0
        })
      } else {
        setError(data.message || 'Failed to add supplier')
      }
    } catch (e: any) {
      setError(e.message || 'Failed to add supplier')
    } finally {
      setLoading(false)
    }
  }

  const toggleStandard = (standard: string) => {
    setFormData(prev => ({
      ...prev,
      standards: prev.standards.includes(standard)
        ? prev.standards.filter(s => s !== standard)
        : [...prev.standards, standard]
    }))
  }

  return (
    <div
      className={`${componentScope('AddSupplierModal')} ${modalStyles.backdrop}`}
      role="presentation"
      onClick={onClose}
    >
      <div
        className={modalStyles.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-supplier-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className={modalStyles.header}>
          <h2 id="add-supplier-modal-title" className={modalStyles.title}>
            {t('tpsm.inventory.addSupplier', 'Add Supplier')}
          </h2>
          <Button
            type="button"
            color="outline"
            iconOnly
            onClick={onClose}
            className={modalStyles.closeBtn}
            aria-label={t('common.close', 'Close')}
            icon={<X className="h-5 w-5" aria-hidden />}
          >
            <span className="sr-only">{t('common.close', 'Close')}</span>
          </Button>
        </div>

        <form onSubmit={handleSubmit} className={modalStyles.form}>
          {error && (
            <div className={modalStyles.error} role="alert">
              {error}
            </div>
          )}

          <TextField
            label={
              <>
                {t('tpsm.inventory.supplierName', 'Supplier Name')} <span aria-hidden>*</span>
              </>
            }
            required
            value={formData.name}
            onChange={(v) => setFormData({ ...formData, name: v })}
            placeholder={t('tpsm.inventory.supplierNamePlaceholder', 'Enter supplier name')}
          />

          <div className={modalStyles.grid2}>
            <TextField
              label={t('tpsm.inventory.contactName', 'Contact Name')}
              value={formData.contactName}
              onChange={(v) => setFormData({ ...formData, contactName: v })}
              autoComplete="name"
            />
            <TextField
              label={t('tpsm.inventory.email', 'Email')}
              type="email"
              value={formData.email}
              onChange={(v) => setFormData({ ...formData, email: v })}
              autoComplete="email"
            />
          </div>

          <TextField
            label={t('tpsm.inventory.website', 'Website')}
            type="url"
            value={formData.website}
            onChange={(v) => setFormData({ ...formData, website: v })}
            placeholder="https://"
            autoComplete="url"
          />

          <div className={modalStyles.grid2}>
            <Select<string>
              label={t('tpsm.inventory.categoryAccess', 'Category Access')}
              options={[...CATEGORY_ACCESS_OPTIONS]}
              value={formData.categoryAccess || null}
              onChange={(v) => setFormData({ ...formData, categoryAccess: v ?? '' })}
              minWidth="100%"
            />
            <Select<string>
              label={t('tpsm.inventory.supplierType', 'Supplier Type')}
              options={[...SUPPLIER_TYPE_OPTIONS]}
              value={formData.supplierType || null}
              onChange={(v) => setFormData({ ...formData, supplierType: v ?? '' })}
              minWidth="100%"
            />
          </div>

          <TextField
            label={`${t('tpsm.inventory.compliance', 'Compliance Score')} (0–100)`}
            type="number"
            min={0}
            max={100}
            value={String(formData.compliance)}
            onChange={(v) => {
              const n = parseInt(v, 10)
              setFormData({
                ...formData,
                compliance: Number.isFinite(n) ? Math.min(100, Math.max(0, n)) : 0,
              })
            }}
          />

          <div className={modalStyles.standardsSection}>
            <span className={modalStyles.standardsLabel}>{t('tpsm.inventory.standards', 'Standards')}</span>
            <div className={modalStyles.standardsBox}>
              <div className={modalStyles.standardsGrid}>
                {availableStandards.map(std => (
                  <label key={std} className={modalStyles.checkboxRow}>
                    <input
                      type="checkbox"
                      checked={formData.standards.includes(std)}
                      onChange={() => toggleStandard(std)}
                      className={modalStyles.checkbox}
                    />
                    <span className={modalStyles.checkboxText}>{std}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className={modalStyles.footer}>
            <Button type="button" color="outline" onClick={onClose} disabled={loading}>
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button type="submit" color="primary" disabled={loading}>
              {loading ? t('common.saving', 'Saving...') : t('common.save', 'Save')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default AddSupplierModal
